import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibTabsNew, KibTabsNewItem } from '@chewy/kib-content-groups-react'
import classNames from 'classnames'
import { LanguageUtils } from '@pbt/pbt-ui-components'
import { formatDate } from '@pbt/pbt-ui-components/src/utils/dateUtils'

import { Type } from '~/api/graphql/generated/types'
import { useRightRailOverlay } from '~/components/common/RightRailOverlayContext'
import PatientDetailsHeader from '~/components/dashboard/soapV2/summary/patient-details/PatientDetailsHeader'
import { PatientMedicalSummaryRail } from '~/components/dashboard/soapV2/summary/PatientMedicalSummaryRail'
import { getConversationById } from '~/store/reducers/conversations'
import { getPatient } from '~/store/reducers/patients'
import { getUser } from '~/store/reducers/users'

import { useChatContainer } from '../context/ChatContainerContext'
import AppointmentDetailsTab from './AppointmentDetailsTab'
import AttachmentPreview from './AttachmentPreview'
import AddPatientButton from './header/AddPatientButton'
import ClientDetailsHeader from './header/ClientDetailsHeader'
import CommunicationsHeaderMenuButton from './header/CommunicationsHeaderMenuButton'
import NonClientHeader from './header/NonClientHeader'

import '@chewy/kib-content-groups-styles/dist/styles/kib-tabs-new.css'
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'
import styles from './CommunicationsPatientMedicalRecordRightRail.module.scss'

interface CommunicationsPatientMedicalRecordRightRailProps {
  conversationId: string
}

enum RightRailTab {
  PatientHistory = 0,
  AppointmentDetails = 1,
}

const CommunicationsPatientMedicalRecordRightRail = ({
  conversationId,
}: CommunicationsPatientMedicalRecordRightRailProps) => {
  const { t } = useTranslation(['Common', 'Communications'])
  const [selectedTab, setSelectedTab] = useState<RightRailTab>(
    RightRailTab.PatientHistory,
  )
  const { selectedAttachment } = useChatContainer()
  const { isOverlayOpen, overlayContent } = useRightRailOverlay()

  const conversation = useSelector(getConversationById(conversationId))
  const clientId = conversation?.client ?? ''
  const patientId = conversation?.patient ?? ''
  const linkedAppointment = conversation?.event
  const soapId = conversation?.soapId || conversation?.event?.soaps?.[0]?.id
  const appointmentId = conversation?.event?.id

  const patient = useSelector(getPatient(patientId))
  const client = useSelector(getUser(clientId))

  const hasLinkedAppointment = Boolean(appointmentId)

  const appointmentTabLabel = useMemo(() => {
    if (!linkedAppointment) {
      return t('Communications:RIGHT_RAIL_TABS.APPOINTMENT_DETAILS')
    }

    const appointmentTypeName = LanguageUtils.getTranslatedFieldName(
      linkedAppointment.businessAppointmentType,
    )
    const formattedDate = linkedAppointment.scheduledStartDatetime
      ? formatDate(linkedAppointment.scheduledStartDatetime)
      : ''

    const baseLabel =
      appointmentTypeName || t('Communications:RIGHT_RAIL_TABS.APPOINTMENT')
    return formattedDate ? `${baseLabel} ${formattedDate}` : baseLabel
  }, [linkedAppointment, t])

  const handleTabChange = (_event: unknown, tabId: number | undefined) => {
    setSelectedTab(tabId ?? RightRailTab.PatientHistory)
  }

  const isClientCommunication = conversation?.recipient?.type === Type.Client

  useEffect(() => {
    if (selectedAttachment) {
      setSelectedTab(RightRailTab.AppointmentDetails)
    }
  }, [selectedAttachment])

  if (!conversation || (!clientId && isClientCommunication)) {
    return null
  }

  return (
    <div className={styles.rightRailContainer}>
      <div
        className={classNames(styles.patientDetailsHeader, {
          [styles.patientDetailsHeaderWithTabs]: hasLinkedAppointment,
        })}
      >
        {!isClientCommunication && (
          <NonClientHeader
            contactId={conversation.contact}
            patientId={patientId}
            recipient={conversation.recipient}
            transport={conversation.transport}
          />
        )}
        {isClientCommunication && !patientId && (
          <ClientDetailsHeader
            actionsSlot={
              <CommunicationsHeaderMenuButton conversation={conversation} />
            }
            clientId={clientId}
          />
        )}
        {patient ? (
          <PatientDetailsHeader
            actionsSlot={
              <CommunicationsHeaderMenuButton conversation={conversation} />
            }
            clientId={clientId}
            patient={patient}
          />
        ) : (
          <AddPatientButton client={client} conversationId={conversationId} />
        )}
      </div>
      {patient && hasLinkedAppointment && (
        <div
          className={styles.tabsContainer}
          data-testid="right-rail-tabs-container"
        >
          <KibTabsNew
            key={`right-rail-tabs-appointment-${linkedAppointment?.id}`}
            selected={selectedTab}
            onTabChange={handleTabChange}
          >
            <KibTabsNewItem
              className={styles.tabsContent}
              label={t('Communications:RIGHT_RAIL_TABS.PATIENT_HISTORY')}
            >
              {isOverlayOpen ? (
                <div className={styles.overlayContainer}>{overlayContent}</div>
              ) : (
                <PatientMedicalSummaryRail
                  clientId={clientId}
                  patientId={patientId}
                />
              )}
            </KibTabsNewItem>
            <KibTabsNewItem
              className={styles.tabsContent}
              label={appointmentTabLabel}
            >
              {selectedAttachment ? (
                <AttachmentPreview />
              ) : (
                <AppointmentDetailsTab
                  appointmentId={appointmentId}
                  businessId={conversation?.businessId}
                  soapId={soapId}
                />
              )}
            </KibTabsNewItem>
          </KibTabsNew>
        </div>
      )}
      {!hasLinkedAppointment && selectedAttachment && <AttachmentPreview />}
      {!hasLinkedAppointment && !selectedAttachment && patient && (
        <div className={styles.medicalRecordContainer}>
          {isOverlayOpen ? (
            <div className={styles.overlayContainer}>{overlayContent}</div>
          ) : (
            <PatientMedicalSummaryRail
              clientId={clientId}
              patientId={patientId}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default CommunicationsPatientMedicalRecordRightRail
